import { useEffect, useState, useRef } from 'react'
import { useNavigate, Link } from '@tanstack/react-router'
import {
  ArrowLeft,
  Loader2,
  Plus,
  X,
  Upload,
  Trash2,
  Clock,
  ImagePlus,
  Star,
  Building2,
  History,
  Check,
  Accessibility,
  Info,
} from 'lucide-react'
import { useAuthStore } from '../lib/auth'
import {
  api,
  type Room,
  type CreateRoomData,
  type RoomSchedule,
  type Blackout,
  type OrgSettings,
  type Building,
  type RoomActivity,
} from '../lib/api'
import { RichTextEditor } from '../components/RichTextEditor'
import { DashboardLayout } from '../components/DashboardLayout'
import {
  ACCESSIBILITY_CATEGORY_LABELS,
  getAccessibilityCategories,
  getAccessibilityFeaturesByCategory,
  getAccessibilityFeature,
} from '@roomsi/shared'

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  AUD: 'A$',
  CAD: 'C$',
  NZD: 'NZ$',
  JPY: '¥',
  CHF: 'CHF',
  SGD: 'S$',
  HKD: 'HK$',
}

interface RoomFormProps {
  roomId: string
}

export function RoomFormPage({ roomId }: RoomFormProps) {
  const navigate = useNavigate()
  const {
    user,
    currentOrg,
    isAuthenticated,
    isLoading: authLoading,
    checkAuth,
    refreshAuth,
  } = useAuthStore()

  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [hasJustSaved, setHasJustSaved] = useState(false)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState<'details' | 'availability' | 'activity'>('details')

  // Track original form data for dirty detection
  const [originalFormData, setOriginalFormData] = useState<string>('')

  // Form state
  const [name, setName] = useState('')
  const [descriptionHtml, setDescriptionHtml] = useState('')
  const [capacity, setCapacity] = useState(1)
  const [hourlyRate, setHourlyRate] = useState(0)
  const [location, setLocation] = useState('')
  const [amenities, setAmenities] = useState<string[]>([])
  const [newAmenity, setNewAmenity] = useState('')

  // Booking settings (null = use org default)
  const [bookingLeadTime, setBookingLeadTime] = useState<number | null>(null)
  const [maxBookingDuration, setMaxBookingDuration] = useState<number | null>(null)
  const [requiresApproval, setRequiresApproval] = useState<boolean | null>(null)

  // Accessibility (null = inherit from building)
  const [accessibilityFeatures, setAccessibilityFeatures] = useState<string[] | null>(null)
  const [accessibilityNotes, setAccessibilityNotes] = useState<string | null>(null)
  const [inheritAccessibility, setInheritAccessibility] = useState(true)

  // Visibility
  const [isPublic, setIsPublic] = useState(false)

  // Building
  const [buildingId, setBuildingId] = useState<string | null>(null)
  const [buildings, setBuildings] = useState<Building[]>([])

  // Images
  const [images, setImages] = useState<{ url: string; alt?: string }[]>([])
  const [heroImageIndex, setHeroImageIndex] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dropZoneRef = useRef<HTMLDivElement>(null)

  // Availability
  const [schedules, setSchedules] = useState<RoomSchedule[]>([])
  const [blackouts, setBlackouts] = useState<Blackout[]>([])
  const [usingBuildingHours, setUsingBuildingHours] = useState(false)

  // Compute dirty state by comparing current form data to original
  const getCurrentFormData = () =>
    JSON.stringify({
      name,
      descriptionHtml,
      capacity,
      hourlyRate,
      location,
      amenities,
      bookingLeadTime,
      maxBookingDuration,
      requiresApproval,
      isPublic,
      buildingId,
      accessibilityFeatures,
      accessibilityNotes,
      inheritAccessibility,
    })
  const isDirty = originalFormData !== '' && getCurrentFormData() !== originalFormData

  // New blackout form
  const [newBlackoutStart, setNewBlackoutStart] = useState('')
  const [newBlackoutEnd, setNewBlackoutEnd] = useState('')
  const [newBlackoutReason, setNewBlackoutReason] = useState('')

  // Org settings (for currency)
  const [orgSettings, setOrgSettings] = useState<OrgSettings>({})

  // Activity log
  const [activities, setActivities] = useState<RoomActivity[]>([])
  const [isLoadingActivity, setIsLoadingActivity] = useState(false)

  useEffect(() => {
    checkAuth()
  }, [])

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate({ to: '/login' })
    }
  }, [authLoading, isAuthenticated, navigate])

  // Wait for both auth to complete AND be authenticated before loading data
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadOrgSettings()
      loadBuildings()
      if (roomId) {
        loadRoom()
      }
    }
  }, [authLoading, isAuthenticated, roomId])

  const loadOrgSettings = async () => {
    try {
      const data = await api.settings.get()
      setOrgSettings(data.organisation.settings || {})
    } catch (err) {
      // Ignore - will use defaults
    }
  }

  const loadBuildings = async () => {
    try {
      const data = await api.buildings.list()
      setBuildings(data.buildings)
    } catch (err) {
      // Ignore - buildings feature may be unavailable
    }
  }

  const loadRoom = async () => {
    if (!roomId) return

    try {
      setIsLoading(true)
      const [roomData, blackoutsData] = await Promise.all([
        api.rooms.get(roomId),
        api.rooms.getBlackouts(roomId),
      ])
      setName(roomData.room.name)
      setDescriptionHtml(roomData.room.descriptionHtml || '')
      setCapacity(roomData.room.capacity)
      setHourlyRate(roomData.room.hourlyRate)
      setLocation(roomData.room.location || '')
      setAmenities(roomData.room.amenities || [])
      setImages(roomData.room.images || [])
      setHeroImageIndex(roomData.room.heroImageIndex ?? 0)
      setBlackouts(blackoutsData.blackouts || [])
      // Booking settings
      setBookingLeadTime(roomData.room.bookingLeadTime ?? null)
      setMaxBookingDuration(roomData.room.maxBookingDuration ?? null)
      setRequiresApproval(roomData.room.requiresApproval ?? null)
      setIsPublic(roomData.room.isPublic ?? false)
      setBuildingId(roomData.room.buildingId ?? null)
      // Accessibility
      const hasRoomAccessibility =
        roomData.room.accessibilityFeatures !== null &&
        roomData.room.accessibilityFeatures !== undefined
      setAccessibilityFeatures(roomData.room.accessibilityFeatures ?? null)
      setAccessibilityNotes(roomData.room.accessibilityNotes ?? null)
      setInheritAccessibility(!hasRoomAccessibility)

      // If room has no schedules but has a building, inherit building opening hours
      if ((!roomData.schedules || roomData.schedules.length === 0) && roomData.room.buildingId) {
        try {
          const buildingSchedules = await api.buildings.getOpeningSchedules(
            roomData.room.buildingId
          )
          if (buildingSchedules.schedules.length > 0) {
            // Convert building opening schedules to room schedule format
            const inheritedSchedules = buildingSchedules.schedules.map((bs) => ({
              id: '',
              roomId: roomId,
              dayOfWeek: bs.dayOfWeek,
              openTime: bs.openTime,
              closeTime: bs.closeTime,
              isActive: bs.isActive,
            }))
            setSchedules(inheritedSchedules)
            setUsingBuildingHours(true)
          } else {
            setSchedules([])
            setUsingBuildingHours(false)
          }
        } catch {
          setSchedules([])
          setUsingBuildingHours(false)
        }
      } else {
        setSchedules(roomData.schedules || [])
        setUsingBuildingHours(false)
      }

      // Store original form data for dirty detection (after state updates settle)
      setTimeout(() => {
        const hasRoomAccessibility =
          roomData.room.accessibilityFeatures !== null &&
          roomData.room.accessibilityFeatures !== undefined
        setOriginalFormData(
          JSON.stringify({
            name: roomData.room.name,
            descriptionHtml: roomData.room.descriptionHtml || '',
            capacity: roomData.room.capacity,
            hourlyRate: roomData.room.hourlyRate,
            location: roomData.room.location || '',
            amenities: roomData.room.amenities || [],
            bookingLeadTime: roomData.room.bookingLeadTime ?? null,
            maxBookingDuration: roomData.room.maxBookingDuration ?? null,
            requiresApproval: roomData.room.requiresApproval ?? null,
            isPublic: roomData.room.isPublic ?? false,
            buildingId: roomData.room.buildingId ?? null,
            accessibilityFeatures: roomData.room.accessibilityFeatures ?? null,
            accessibilityNotes: roomData.room.accessibilityNotes ?? null,
            inheritAccessibility: !hasRoomAccessibility,
          })
        )
      }, 0)
    } catch (err: any) {
      setError(err.message || 'Failed to load room')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!name.trim()) {
      setError('Room name is required')
      return
    }

    setIsSaving(true)

    try {
      const data: CreateRoomData = {
        name: name.trim(),
        descriptionHtml: descriptionHtml || undefined,
        capacity,
        hourlyRate,
        location: location.trim() || undefined,
        amenities: amenities.length > 0 ? amenities : undefined,
        bookingLeadTime,
        maxBookingDuration,
        requiresApproval,
        isPublic,
        buildingId: buildingId || undefined,
        // Accessibility: null = inherit from building, empty array = explicitly no features
        accessibilityFeatures: inheritAccessibility ? null : accessibilityFeatures || [],
        accessibilityNotes: inheritAccessibility ? null : accessibilityNotes || null,
      }

      await api.rooms.update(roomId, data)

      // Update original form data to current (no longer dirty)
      setOriginalFormData(getCurrentFormData())

      // Show saved state
      setHasJustSaved(true)
      setTimeout(() => setHasJustSaved(false), 2000)
    } catch (err: any) {
      setError(err.message || 'Failed to save room')
    } finally {
      setIsSaving(false)
    }
  }

  const addAmenity = () => {
    if (newAmenity.trim() && !amenities.includes(newAmenity.trim())) {
      setAmenities([...amenities, newAmenity.trim()])
      setNewAmenity('')
    }
  }

  const removeAmenity = (amenity: string) => {
    setAmenities(amenities.filter((a) => a !== amenity))
  }

  // Image handlers
  const uploadFiles = async (files: File[]) => {
    if (!files.length) return

    // Filter for valid image types
    const validFiles = files.filter((f) =>
      ['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(f.type)
    )

    if (!validFiles.length) {
      setError('Please select valid image files (JPEG, PNG, WebP, GIF)')
      return
    }

    setIsUploading(true)
    setError('')

    try {
      const result = await api.rooms.uploadImages(roomId, validFiles)
      setImages(result.images)
    } catch (err: any) {
      setError(err.message || 'Failed to upload images')
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return
    await uploadFiles(Array.from(e.target.files))
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Only set to false if we're leaving the drop zone entirely
    if (!dropZoneRef.current?.contains(e.relatedTarget as Node)) {
      setIsDragging(false)
    }
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = Array.from(e.dataTransfer.files) as File[]
    await uploadFiles(files)
  }

  const handleDeleteImage = async (url: string) => {
    const filename = url.split('/').pop()
    if (!filename) return

    try {
      const result = await api.rooms.deleteImage(roomId, filename)
      setImages(result.images)
      setHeroImageIndex(result.heroImageIndex)
    } catch (err: any) {
      setError(err.message || 'Failed to delete image')
    }
  }

  const handleSetHeroImage = async (index: number) => {
    try {
      await api.rooms.setHeroImage(roomId, index)
      setHeroImageIndex(index)
    } catch (err: any) {
      setError(err.message || 'Failed to set hero image')
    }
  }

  // Schedule handlers
  const handleScheduleToggle = (dayOfWeek: number, isActive: boolean) => {
    setSchedules((prev) => {
      const existing = prev.find((s) => s.dayOfWeek === dayOfWeek)
      if (existing) {
        return prev.map((s) => (s.dayOfWeek === dayOfWeek ? { ...s, isActive } : s))
      }
      return [
        ...prev,
        {
          id: '',
          roomId: roomId || '',
          dayOfWeek,
          openTime: '09:00',
          closeTime: '17:00',
          isActive,
        },
      ]
    })
  }

  const handleScheduleTimeChange = (
    dayOfWeek: number,
    field: 'openTime' | 'closeTime',
    value: string
  ) => {
    setSchedules((prev) =>
      prev.map((s) => (s.dayOfWeek === dayOfWeek ? { ...s, [field]: value } : s))
    )
  }

  const handleSaveSchedule = async () => {
    if (!roomId) return

    try {
      const scheduleData = schedules.map(({ dayOfWeek, openTime, closeTime, isActive }) => ({
        dayOfWeek,
        openTime,
        closeTime,
        isActive,
      }))
      const result = await api.rooms.updateSchedule(roomId, scheduleData)
      setSchedules(result.schedules)
      setUsingBuildingHours(false) // Once saved, they're room-specific
    } catch (err: any) {
      setError(err.message || 'Failed to save schedule')
    }
  }

  const handleResetToBuildingHours = async () => {
    if (!buildingId) return

    try {
      const buildingSchedules = await api.buildings.getOpeningSchedules(buildingId)
      if (buildingSchedules.schedules.length > 0) {
        const inheritedSchedules = buildingSchedules.schedules.map((bs) => ({
          id: '',
          roomId: roomId || '',
          dayOfWeek: bs.dayOfWeek,
          openTime: bs.openTime,
          closeTime: bs.closeTime,
          isActive: bs.isActive,
        }))
        setSchedules(inheritedSchedules)
        setUsingBuildingHours(true)
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load building hours')
    }
  }

  // Blackout handlers
  const handleAddBlackout = async () => {
    if (!roomId || !newBlackoutStart || !newBlackoutEnd) return

    try {
      const result = await api.rooms.createBlackout(roomId, {
        startDate: new Date(newBlackoutStart).toISOString(),
        endDate: new Date(newBlackoutEnd).toISOString(),
        reason: newBlackoutReason.trim() || undefined,
      })
      setBlackouts((prev) => [...prev, result.blackout])
      setNewBlackoutStart('')
      setNewBlackoutEnd('')
      setNewBlackoutReason('')
    } catch (err: any) {
      setError(err.message || 'Failed to add blackout')
    }
  }

  const handleDeleteBlackout = async (blackoutId: string) => {
    if (!roomId) return

    try {
      await api.rooms.deleteBlackout(roomId, blackoutId)
      setBlackouts((prev) => prev.filter((b) => b.id !== blackoutId))
    } catch (err: any) {
      setError(err.message || 'Failed to delete blackout')
    }
  }

  const loadActivity = async () => {
    if (!roomId) return

    setIsLoadingActivity(true)
    try {
      const result = await api.rooms.getActivity(roomId, 100)
      setActivities(result.activities)
    } catch (err: any) {
      console.error('Failed to load activity:', err)
    } finally {
      setIsLoadingActivity(false)
    }
  }

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!isAuthenticated || !user) {
    return null
  }

  const backButton = (
    <Link
      to="/dashboard/rooms"
      className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
    >
      <ArrowLeft className="w-5 h-5" />
    </Link>
  )

  return (
    <DashboardLayout title="Edit Room" actions={backButton}>
      <div className="max-w-4xl">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex gap-4">
            <button
              type="button"
              onClick={() => setActiveTab('details')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'details'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Details
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('availability')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'availability'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Clock className="w-4 h-4 inline-block mr-1" />
              Availability
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('activity')
                loadActivity()
              }}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'activity'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <History className="w-4 h-4 inline-block mr-1" />
              Activity
            </button>
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Room Images Section */}
          {activeTab === 'details' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 p-3 bg-primary-100 rounded-lg">
                  <ImagePlus className="w-6 h-6 text-gray-900" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-lg font-semibold text-gray-900">Room Images</h2>
                    <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
                      <Star className="w-3 h-3" />
                      Recommended
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Upload up to 5 photos to showcase your room. Click the star to select which
                    image appears as the hero in listings.
                  </p>

                  <div
                    ref={dropZoneRef}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`relative transition-colors ${isDragging ? 'ring-2 ring-primary-500 ring-offset-2 rounded-lg' : ''}`}
                  >
                    {isDragging && (
                      <div className="absolute inset-0 bg-primary-50/90 border-2 border-dashed border-primary-500 rounded-lg z-10 flex items-center justify-center">
                        <div className="text-center">
                          <Upload className="w-8 h-8 text-primary-600 mx-auto mb-2" />
                          <p className="text-primary-700 font-medium">Drop images here</p>
                        </div>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                    />

                    {images.length > 0 ? (
                      <div className="space-y-4">
                        {/* Image grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                          {images.map((image, index) => (
                            <div key={image.url} className="relative group aspect-square">
                              <div
                                className={`w-full h-full rounded-lg overflow-hidden bg-gray-100 border-2 ${index === heroImageIndex ? 'border-primary-500 ring-2 ring-primary-200' : 'border-gray-200'}`}
                              >
                                <img
                                  src={image.url}
                                  alt={image.alt || `Room image ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                {/* Overlay with actions */}
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                    <button
                                      type="button"
                                      onClick={() => handleSetHeroImage(index)}
                                      className={`p-2 rounded-lg ${index === heroImageIndex ? 'bg-primary-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                                      title={
                                        index === heroImageIndex ? 'Hero image' : 'Set as hero'
                                      }
                                    >
                                      <Star
                                        className={`w-4 h-4 ${index === heroImageIndex ? 'fill-current' : ''}`}
                                      />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteImage(image.url)}
                                      className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                                      title="Delete image"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                              {/* Hero badge */}
                              {index === heroImageIndex && (
                                <div className="absolute -top-2 -left-2 px-2 py-0.5 bg-primary-600 text-white text-xs font-medium rounded flex items-center gap-1">
                                  <Star className="w-3 h-3 fill-current" />
                                  Hero
                                </div>
                              )}
                            </div>
                          ))}
                          {/* Add more button (if less than 5 images) */}
                          {images.length < 5 && (
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={isUploading}
                              className="aspect-square w-full flex flex-col items-center justify-center gap-2 bg-white border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50/50 transition-colors disabled:opacity-50"
                            >
                              {isUploading ? (
                                <Loader2 className="w-6 h-6 animate-spin" />
                              ) : (
                                <>
                                  <Plus className="w-6 h-6" />
                                  <span className="text-xs">Add Photo</span>
                                </>
                              )}
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          {images.length}/5 images • JPEG, PNG, WebP up to 10MB each
                        </p>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        className="flex items-center gap-3 px-6 py-4 bg-white border-2 border-dashed border-primary-300 rounded-lg text-primary-700 hover:border-primary-400 hover:bg-primary-50/50 transition-colors disabled:opacity-50 w-full max-w-md"
                      >
                        {isUploading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Upload className="w-5 h-5" />
                        )}
                        <div className="text-left">
                          <div className="font-medium">
                            {isUploading ? 'Uploading...' : 'Upload Room Images'}
                          </div>
                          <div className="text-xs text-gray-500">
                            Drag & drop or click • JPEG, PNG, WebP up to 10MB • Max 5 images
                          </div>
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Details Tab */}
          {activeTab === 'details' && (
            <>
              <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
                <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Room Name *
                  </label>
                  <input
                    id="name"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Conference Room A"
                  />
                </div>

                <div>
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Description
                  </label>
                  <RichTextEditor
                    content={descriptionHtml}
                    onChange={setDescriptionHtml}
                    placeholder="Describe the room, its features, and any special instructions..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="capacity"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Capacity
                    </label>
                    <input
                      id="capacity"
                      type="number"
                      min={1}
                      value={capacity}
                      onChange={(e) => setCapacity(parseInt(e.target.value) || 1)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="hourlyRate"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Hourly Rate ({CURRENCY_SYMBOLS[orgSettings.currency || 'USD'] || '$'})
                    </label>
                    <input
                      id="hourlyRate"
                      type="number"
                      min={0}
                      step={0.01}
                      value={hourlyRate}
                      onChange={(e) => setHourlyRate(parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                {/* Building selector */}
                {buildings.length > 0 && (
                  <div>
                    <label
                      htmlFor="building"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Building
                    </label>
                    <select
                      id="building"
                      value={buildingId || ''}
                      onChange={(e) => setBuildingId(e.target.value || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">No building assigned</option>
                      {buildings.map((building) => (
                        <option key={building.id} value={building.id}>
                          {building.name}
                          {building.city ? ` (${building.city})` : ''}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Assign this room to a building to inherit its address and cleaning schedule
                    </p>
                  </div>
                )}

                {/* Show building location info if building is selected, otherwise show location input */}
                {buildingId ? (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      Location (from building)
                    </div>
                    {(() => {
                      const selectedBuilding = buildings.find((b) => b.id === buildingId)
                      if (!selectedBuilding)
                        return <p className="text-gray-500 text-sm">Building not found</p>
                      return (
                        <div className="space-y-2 text-sm text-gray-600">
                          {selectedBuilding.address && (
                            <p>
                              {[
                                selectedBuilding.address,
                                selectedBuilding.city,
                                selectedBuilding.postcode,
                                selectedBuilding.country,
                              ]
                                .filter(Boolean)
                                .join(', ')}
                            </p>
                          )}
                          {selectedBuilding.googleMapsUrl && (
                            <a
                              href={selectedBuilding.googleMapsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-700 inline-flex items-center gap-1"
                            >
                              View on Google Maps →
                            </a>
                          )}
                          {selectedBuilding.directions && (
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              <div className="font-medium text-gray-700 mb-1">How to get here:</div>
                              <p className="whitespace-pre-wrap">{selectedBuilding.directions}</p>
                            </div>
                          )}
                          {!selectedBuilding.address &&
                            !selectedBuilding.googleMapsUrl &&
                            !selectedBuilding.directions && (
                              <p className="text-gray-400 italic">
                                No location details set for this building
                              </p>
                            )}
                        </div>
                      )
                    })()}
                  </div>
                ) : (
                  <div>
                    <label
                      htmlFor="location"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Location
                    </label>
                    <input
                      id="location"
                      type="text"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Building A, Floor 2 or Google Maps URL"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Enter a location name or paste a Google Maps link (or assign a building above)
                    </p>
                  </div>
                )}

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <label htmlFor="isPublic" className="text-sm font-medium text-gray-900">
                        Public Room
                      </label>
                      <p className="text-sm text-gray-500">
                        Make this room visible to guests and show it on the public booking page
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsPublic(!isPublic)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                        isPublic ? 'bg-primary-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          isPublic ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
                <h2 className="text-lg font-semibold text-gray-900">Amenities</h2>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newAmenity}
                    onChange={(e) => setNewAmenity(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addAmenity())}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="WiFi, Projector, Whiteboard..."
                  />
                  <button
                    type="button"
                    onClick={addAmenity}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>

                {amenities.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {amenities.map((amenity) => (
                      <span
                        key={amenity}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm"
                      >
                        {amenity}
                        <button
                          type="button"
                          onClick={() => removeAmenity(amenity)}
                          className="p-0.5 hover:bg-primary-100 rounded-full"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Accessibility Features */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      <Accessibility className="w-5 h-5 inline-block mr-2" />
                      Accessibility Features
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {buildingId
                        ? 'By default, accessibility features are inherited from the building. Toggle to customise for this room.'
                        : 'Configure accessibility features for this room.'}
                    </p>
                  </div>
                  {buildingId && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-sm text-gray-600">Inherit from building</span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={inheritAccessibility}
                        onClick={() => {
                          if (inheritAccessibility) {
                            // Switching to custom: copy building features if available
                            const building = buildings.find((b) => b.id === buildingId)
                            setAccessibilityFeatures(building?.accessibilityFeatures || [])
                            setAccessibilityNotes(building?.accessibilityNotes || null)
                          } else {
                            // Switching back to inherit
                            setAccessibilityFeatures(null)
                            setAccessibilityNotes(null)
                          }
                          setInheritAccessibility(!inheritAccessibility)
                        }}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                          inheritAccessibility ? 'bg-primary-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            inheritAccessibility ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </label>
                  )}
                </div>

                {inheritAccessibility && buildingId ? (
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-blue-800">
                          Accessibility features are inherited from the building.
                        </p>
                        {(() => {
                          const building = buildings.find((b) => b.id === buildingId)
                          const features = building?.accessibilityFeatures || []
                          if (features.length > 0) {
                            return (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {features.map((featureId) => {
                                  const feature = getAccessibilityFeature(featureId)
                                  return feature ? (
                                    <span
                                      key={featureId}
                                      className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs"
                                    >
                                      {feature.label}
                                    </span>
                                  ) : null
                                })}
                              </div>
                            )
                          }
                          return (
                            <p className="text-sm text-blue-700 mt-1">
                              No accessibility features configured on the building.
                            </p>
                          )
                        })()}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {getAccessibilityCategories().map((category) => (
                      <div key={category} className="mb-4">
                        <h3 className="text-sm font-medium text-gray-700 mb-2">
                          {ACCESSIBILITY_CATEGORY_LABELS[category]}
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {getAccessibilityFeaturesByCategory(category).map((feature) => (
                            <label
                              key={feature.id}
                              className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={(accessibilityFeatures || []).includes(feature.id)}
                                onChange={(e) => {
                                  const current = accessibilityFeatures || []
                                  if (e.target.checked) {
                                    setAccessibilityFeatures([...current, feature.id])
                                  } else {
                                    setAccessibilityFeatures(
                                      current.filter((id) => id !== feature.id)
                                    )
                                  }
                                }}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-0.5"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">
                                  {feature.label}
                                </div>
                                <div className="text-xs text-gray-500">{feature.description}</div>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}

                    <div className="pt-4 border-t border-gray-200">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Additional Accessibility Notes{' '}
                        <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <textarea
                        value={accessibilityNotes || ''}
                        onChange={(e) => setAccessibilityNotes(e.target.value || null)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Describe any additional accessibility accommodations or limitations specific to this room..."
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
                <h2 className="text-lg font-semibold text-gray-900">Booking Settings</h2>
                <p className="text-sm text-gray-500">
                  Override the default organisation settings for this room. Leave empty to use the
                  organisation defaults
                  {orgSettings.bookingLeadTime !== undefined && (
                    <>
                      {' '}
                      (Lead time: {orgSettings.bookingLeadTime}h, Max duration:{' '}
                      {orgSettings.maxBookingDuration || 8}h)
                    </>
                  )}
                  .
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="bookingLeadTime"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Minimum Lead Time (minutes)
                    </label>
                    <input
                      id="bookingLeadTime"
                      type="number"
                      min={0}
                      value={bookingLeadTime ?? ''}
                      onChange={(e) =>
                        setBookingLeadTime(e.target.value === '' ? null : parseInt(e.target.value))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder={
                        orgSettings.bookingLeadTime !== undefined
                          ? `${orgSettings.bookingLeadTime * 60} (org default)`
                          : '0 (org default)'
                      }
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      How far in advance bookings must be made
                    </p>
                  </div>

                  <div>
                    <label
                      htmlFor="maxBookingDuration"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Maximum Duration (minutes)
                    </label>
                    <input
                      id="maxBookingDuration"
                      type="number"
                      min={1}
                      value={maxBookingDuration ?? ''}
                      onChange={(e) =>
                        setMaxBookingDuration(
                          e.target.value === '' ? null : parseInt(e.target.value)
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder={
                        orgSettings.maxBookingDuration !== undefined
                          ? `${orgSettings.maxBookingDuration * 60} (org default)`
                          : '480 (org default)'
                      }
                    />
                    <p className="mt-1 text-xs text-gray-500">Maximum allowed booking duration</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex items-center">
                    <input
                      id="requiresApproval"
                      type="checkbox"
                      checked={requiresApproval === true}
                      onChange={(e) => setRequiresApproval(e.target.checked ? true : null)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="requiresApproval" className="ml-2 block text-sm text-gray-900">
                      Requires Approval
                    </label>
                  </div>
                  {requiresApproval === null && (
                    <span className="text-xs text-gray-500">
                      (using org default: {orgSettings.requireApprovalByDefault ? 'Yes' : 'No'})
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  If checked, bookings for this room require admin approval
                </p>
              </div>
            </>
          )}

          {/* Availability Tab */}
          {activeTab === 'availability' && (
            <div className="space-y-6">
              {/* Regular Hours */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">
                    <Clock className="w-5 h-5 inline-block mr-2" />
                    Regular Hours
                  </h2>
                  {buildingId && (
                    <button
                      type="button"
                      onClick={handleResetToBuildingHours}
                      className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    >
                      <Building2 className="w-4 h-4" />
                      Reset to Building Hours
                    </button>
                  )}
                </div>

                {usingBuildingHours && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <Building2 className="w-4 h-4 inline-block mr-1" />
                      Using building's opening hours. Make changes here to customize for this room
                      only.
                    </p>
                  </div>
                )}

                <div className="space-y-3">
                  {DAYS_OF_WEEK.map((day, index) => {
                    const schedule = schedules.find((s) => s.dayOfWeek === index)
                    return (
                      <div key={day} className="flex items-center gap-4">
                        <div className="w-24 text-sm font-medium text-gray-700">{day}</div>
                        <input
                          type="checkbox"
                          checked={schedule?.isActive ?? false}
                          onChange={(e) => handleScheduleToggle(index, e.target.checked)}
                          className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                        />
                        {schedule?.isActive && (
                          <>
                            <input
                              type="time"
                              value={schedule.openTime}
                              onChange={(e) =>
                                handleScheduleTimeChange(index, 'openTime', e.target.value)
                              }
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                            <span className="text-gray-400">to</span>
                            <input
                              type="time"
                              value={schedule.closeTime}
                              onChange={(e) =>
                                handleScheduleTimeChange(index, 'closeTime', e.target.value)
                              }
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </>
                        )}
                        {!schedule?.isActive && (
                          <span className="text-sm text-gray-400">Closed</span>
                        )}
                      </div>
                    )
                  })}
                </div>
                <button
                  type="button"
                  onClick={handleSaveSchedule}
                  className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
                >
                  Save Hours
                </button>
              </div>

              {/* One-time Blackouts */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Blackouts</h2>
                <p className="text-sm text-gray-500 mb-4">
                  Block specific date ranges (e.g., holidays, renovations).
                </p>

                {/* Add blackout form */}
                <div className="flex flex-wrap gap-3 items-end mb-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <input
                      type="datetime-local"
                      value={newBlackoutStart}
                      onChange={(e) => setNewBlackoutStart(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="datetime-local"
                      value={newBlackoutEnd}
                      onChange={(e) => setNewBlackoutEnd(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Reason</label>
                    <input
                      type="text"
                      value={newBlackoutReason}
                      onChange={(e) => setNewBlackoutReason(e.target.value)}
                      placeholder="Holiday"
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-32"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAddBlackout}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                {/* Blackout list */}
                {blackouts.length > 0 ? (
                  <div className="space-y-2">
                    {blackouts.map((blackout) => (
                      <div
                        key={blackout.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <span className="font-medium text-gray-900">
                            {new Date(blackout.startDate).toLocaleDateString()} -{' '}
                            {new Date(blackout.endDate).toLocaleDateString()}
                          </span>
                          {blackout.reason && (
                            <>
                              <span className="mx-2 text-gray-400">•</span>
                              <span className="text-sm text-gray-600">{blackout.reason}</span>
                            </>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteBlackout(blackout.id)}
                          className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No blackouts configured
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Activity Tab */}
          {activeTab === 'activity' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <History className="w-5 h-5 text-gray-500" />
                Activity Log
              </h3>

              {isLoadingActivity ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                </div>
              ) : activities.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No booking activity yet</div>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Status</th>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Booking</th>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Date/Time</th>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Payment</th>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Booked By</th>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Action By</th>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Updated</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {activities.map((activity) => {
                        // Determine who took the action based on status
                        const actionBy =
                          activity.type === 'booking_confirmed' ||
                          activity.type === 'booking_denied'
                            ? activity.approvedBy
                            : activity.type === 'booking_cancelled'
                              ? activity.cancelledBy
                              : null
                        const refundedBy =
                          activity.booking.paymentStatus === 'refunded' ? activity.refundedBy : null

                        return (
                          <tr key={activity.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  activity.type === 'booking_confirmed'
                                    ? 'bg-green-100 text-green-700'
                                    : activity.type === 'booking_cancelled'
                                      ? 'bg-red-100 text-red-700'
                                      : activity.type === 'booking_denied'
                                        ? 'bg-orange-100 text-orange-700'
                                        : activity.type === 'booking_completed'
                                          ? 'bg-blue-100 text-blue-700'
                                          : 'bg-yellow-100 text-yellow-700'
                                }`}
                              >
                                {activity.type === 'booking_requested' && 'Requested'}
                                {activity.type === 'booking_confirmed' && 'Confirmed'}
                                {activity.type === 'booking_cancelled' && 'Cancelled'}
                                {activity.type === 'booking_denied' && 'Denied'}
                                {activity.type === 'booking_completed' && 'Completed'}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-gray-900">
                              {activity.booking.title || 'Untitled'}
                            </td>
                            <td className="px-4 py-2 text-gray-600">
                              {new Date(activity.booking.startTime).toLocaleDateString()}{' '}
                              {new Date(activity.booking.startTime).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </td>
                            <td className="px-4 py-2">
                              {activity.booking.totalPrice > 0 &&
                              activity.booking.paymentStatus &&
                              activity.booking.paymentStatus !== 'not_required' ? (
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                    activity.booking.paymentStatus === 'paid'
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : activity.booking.paymentStatus === 'refunded'
                                        ? 'bg-teal-100 text-teal-700'
                                        : activity.booking.paymentStatus === 'pending'
                                          ? 'bg-orange-100 text-orange-700'
                                          : activity.booking.paymentStatus === 'failed'
                                            ? 'bg-red-100 text-red-700'
                                            : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  {activity.booking.paymentStatus === 'paid' &&
                                    `Paid £${Number(activity.booking.totalPrice).toFixed(2)}`}
                                  {activity.booking.paymentStatus === 'refunded' &&
                                    `Refunded £${Number(activity.booking.totalPrice).toFixed(2)}`}
                                  {activity.booking.paymentStatus === 'pending' && 'Pending'}
                                  {activity.booking.paymentStatus === 'failed' && 'Failed'}
                                </span>
                              ) : activity.booking.totalPrice > 0 ? (
                                <span className="text-gray-400 text-xs">
                                  £{Number(activity.booking.totalPrice).toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-gray-400 text-xs">Free</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-gray-600">
                              {activity.bookedBy ? (
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {activity.bookedBy.name}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {activity.bookedBy.email}
                                  </div>
                                </div>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td className="px-4 py-2 text-gray-600">
                              {actionBy || refundedBy ? (
                                <div className="space-y-1">
                                  {actionBy && (
                                    <div>
                                      <div className="font-medium text-gray-900">
                                        {actionBy.name}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {activity.type === 'booking_confirmed' && 'Confirmed'}
                                        {activity.type === 'booking_denied' && 'Denied'}
                                        {activity.type === 'booking_cancelled' && 'Cancelled'}
                                      </div>
                                    </div>
                                  )}
                                  {refundedBy && refundedBy.id !== actionBy?.id && (
                                    <div>
                                      <div className="font-medium text-gray-900">
                                        {refundedBy.name}
                                      </div>
                                      <div className="text-xs text-teal-600">Refunded</div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-gray-500 text-xs">
                              {new Date(activity.timestamp).toLocaleDateString()}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Save Buttons - Only visible on editable tabs */}
          {activeTab !== 'activity' && (
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 mt-6">
              {/* Unsaved changes indicator */}
              {isDirty && !isSaving && !hasJustSaved && (
                <span className="text-sm text-amber-600 flex items-center gap-1">
                  <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                  Unsaved changes
                </span>
              )}

              <Link
                to="/dashboard/rooms"
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={isSaving || hasJustSaved || !isDirty}
                className={`px-5 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 min-w-[140px] justify-center ${
                  hasJustSaved
                    ? 'bg-green-600 text-white'
                    : isDirty
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : hasJustSaved ? (
                  <>
                    <Check className="w-4 h-4" />
                    Saved!
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          )}
        </form>
      </div>
    </DashboardLayout>
  )
}

// Wrapper component for edit route
export function EditRoomPage({ roomId }: { roomId: string }) {
  return <RoomFormPage roomId={roomId} />
}
